/* eslint-disable sonarjs/table-header */
import { Button, InputField } from '@salt/components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
// import cx from 'classnames';
import moment from 'moment';
import { getUsers } from '../../api/users';

import MenuIcon from '../../media/icons/menu.svg';
import NextIcon from '../../media/icons/page-next.svg';
import PrevIcon from '../../media/icons/page-prev.svg';
import DeleteUserModal from './DeleteUserModal';
import InviteNewUserModal from './InviteNewUserModal';
import ResendInviteModal from './ResendInviteModal';
import ResetPasswordModal from './ResetPasswordModal';
import InviteLinkInfoModal from './InviteLinkInfoModal';

const PAGE_SIZE = 10;

const UsersScreen = ({ userDetail }) => {
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  const [deleteUserOpen, setDeleteUserOpen] = useState(undefined);
  const [editUserOpen, setEditUserOpen] = useState(undefined);
  const [inviteLinkOpen, setInviteLinkOpen] = useState(undefined);

  const [inviteNewUserOpen, setInviteNewUserOpen] = useState(undefined);
  const [reinviteOpen, setReinviteOpen] = useState(undefined);
  const [resetOpen, setResetOpen] = useState(undefined);

  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ page: 0, keywords: '' });
  const [users, setUsers] = useState([]);
  const [userMenu, setUserMenu] = useState(undefined);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchData = useCallback(async () => {
    // setLoading(true);
    try {
      const { usersItems, uTotal } = await getUsers({
        ...filter,
        skip: filter.page * 10,
      });

      setUsers(usersItems);
      setTotal(uTotal);
    } catch (error) {
      console.error(error.message);
    }
    // setLoading(false);
  }, [filter.page, filter.keywords]);

  const handlePageClick = useCallback(
    (dir) => {
      if (dir === 'next') {
        setFilter({ ...filter, page: filter.page + 1 });
      } else {
        setFilter({ ...filter, page: filter.page - 1 });
      }
    },
    [filter]
  );

  useEffect(() => {
    fetchData();
  }, [filter.page, filter.keywords]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If the ref exists and the click target is NOT inside the menu ref or menu button ref
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setUserMenu(undefined);
      }
    };

    // Only add listener if the element is open
    if (userMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenu]);

  const handleMenuOpen = useCallback(
    (id) => {
      if (userMenu === id) {
        setUserMenu(undefined);
      } else {
        setUserMenu(id);
      }
    },
    [userMenu]
  );

  return (
    <main
      style={{
        width: '100%',
        padding: '25px 50px',
      }}
    >
      <div className="w-full inline-block">
        <h1 className="text-[#434C58] text-[24px] ">User Management</h1>
      </div>
      <br />
      <div className="flex mt-10">
        <div className="search w-[410px]">
          <InputField
            icon="search"
            iconPlacement="left"
            onChange={(event) => setFilter({ ...filter, keywords: event.target.value })}
            onClear={() => setFilter({ ...filter, keywords: '' })}
            placeholder="Search Users"
            value={filter.keywords}
            labelBackgroundClass="peer-focus:bg-secondary peer-[:not(:placeholder-shown)]:bg-secondary"
            backgroundClass="bg-secondary-light"
          />
        </div>
        <Button
          onClick={() => setInviteNewUserOpen(true)}
          type="positive"
          className="max-w-[146px] h-[36px] ml-auto"
          paddingClassName="py-2"
        >
          Invite User
        </Button>
      </div>
      <div className="main-table">
        {users.length > 0 ? (
          <table className="projects-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Customer</th>
                <th>Invited</th>
                <th>Active Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users?.map((user) => (
                <tr
                  key={user.id}
                  className={cx({ '!bg-accent/10': userDetail.email === user.email })}
                >
                  <td>
                    <div className="flex items-center justify-center">
                      <span className="mx-4 my-auto">{user.username}</span>
                      {userDetail.email === user.email && (
                        <div className="bg-[#37C2D7] rounded-full px-2 py-1 text-white">You</div>
                      )}
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>{user.role}</td>
                  <td>{user.customer}</td>
                  <td>{moment(user.invitedAt).format('MMM Do, YYYY, HH:mm')}</td>
                  <td>
                    <div
                      className={cx('rounded-full py-1 px-3 text-center truncate w-[97px]', {
                        'bg-[#FF3366]/10 text-[#FF3366]': user.status !== 'active',
                        'bg-[#00CD8F]/10 text-[#00CD8F]': user.status === 'active',
                      })}
                    >
                      {user.status}
                    </div>
                  </td>
                  <td className="flex !px-4 h-[57px] float-right">
                    <button
                      ref={userMenu === user.id ? buttonRef : undefined}
                      onClick={() => handleMenuOpen(user.id)}
                      className="my-auto"
                      type="button"
                    >
                      <img style={{ width: '100%', height: '100%' }} src={MenuIcon} alt="" />
                    </button>
                    {userMenu === user.id && (
                      <div ref={containerRef} className="absolute right-7 mt-10 z-10 w-[135px]">
                        <div className="bg-white shadow-xl/10">
                          <button
                            type="button"
                            onClick={() => setEditUserOpen(user)}
                            className="block px-4 py-2 text-base font-normal text-gray-700 w-full text-left
                                              hover:bg-gray-100"
                          >
                            Edit
                          </button>
                          {user.status === 'active' && userDetail.email !== user.email && (
                            <button
                              type="button"
                              onClick={() => setResetOpen(user)}
                              className="block px-4 py-2 text-base font-normal text-gray-700 w-full text-left
                                              hover:bg-gray-100"
                            >
                              Reset Password
                            </button>
                          )}
                          {user.status !== 'active' && userDetail.email !== user.email && (
                            <button
                              type="button"
                              onClick={() => setReinviteOpen(user)}
                              className="block px-4 py-2 text-base font-normal text-gray-700 w-full text-left
                                              hover:bg-gray-100"
                            >
                              Resend Invite
                            </button>
                          )}
                          {userDetail.email !== user.email && (
                            <button
                              type="button"
                              onClick={() => setDeleteUserOpen(user)}
                              className="block px-4 py-2 text-base font-normal w-full text-left
                                              hover:bg-gray-100 text-negative"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="reports-table mt-14">
            <tbody>
              <tr>
                <td>No users to display</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <br />

      <div className="flex float-right">
        <div className="text-base flex items-center justify-center mx-2">
          Page {filter.page + 1} of {pageCount}
        </div>
        <button
          type="button"
          onClick={() => handlePageClick('prev')}
          disabled={filter.page + 1 === 1}
          className="bg-accent rounded-md p-2 flex justify-center items-center m-1 disabled:bg-accent/20 disabled:cursor-not-allowed"
        >
          <img className="w-2 h-2 absolute" src={PrevIcon} alt="p" />
        </button>

        <button
          type="button"
          onClick={() => handlePageClick('next')}
          disabled={filter.page + 1 === pageCount}
          className="bg-accent rounded-md p-2 flex justify-center items-center m-1 disabled:bg-accent/20 disabled:cursor-not-allowed"
        >
          <img className="w-2 h-2 absolute" src={NextIcon} alt="n" />
        </button>
      </div>
      {deleteUserOpen && (
        <DeleteUserModal
          user={deleteUserOpen}
          handleDeleteUserModalClose={() => setDeleteUserOpen(undefined)}
          fetchData={fetchData}
        />
      )}

      {editUserOpen && (
        <InviteNewUserModal
          user={editUserOpen}
          handleInviteNewUserModalClose={() => setEditUserOpen(undefined)}
          fetchData={fetchData}
          userDetail={userDetail}
          setInviteLinkOpen={setInviteLinkOpen}
        />
      )}

      {inviteNewUserOpen && (
        <InviteNewUserModal
          handleInviteNewUserModalClose={() => setInviteNewUserOpen(undefined)}
          userDetail={userDetail}
          fetchData={fetchData}
          setInviteLinkOpen={setInviteLinkOpen}
        />
      )}

      {reinviteOpen && (
        <ResendInviteModal
          user={reinviteOpen}
          handleResendInviteModalClose={() => setReinviteOpen(undefined)}
          setInviteLinkOpen={setInviteLinkOpen}
        />
      )}

      {resetOpen && (
        <ResetPasswordModal
          user={resetOpen}
          handleResetPasswordModalClose={() => setResetOpen(undefined)}
        />
      )}

      {inviteLinkOpen && (
        <InviteLinkInfoModal
          handleInviteLinkInfoModalClose={() => setInviteLinkOpen(undefined)}
          inviteLinkOpen={inviteLinkOpen}
        />
      )}
    </main>
  );
};

export default UsersScreen;
